import { alpha, Box, Typography, type SxProps } from '@mui/material';
import { flatSx, FlexColumn, SmOrUp } from '@ocodelib/mui';
import { sxFlexRowCenter } from '@repo-foundation/util/sx-util';
import clsx from 'clsx';
import type { BannerBreadcrumbsProps } from '../BannerBreadcrumbs';
import { BannerBreadcrumbs } from '../BannerBreadcrumbs';
import { PageTabs } from './PageTabs';

const BANNER_HEIGHT = 210;

export interface PageBannerProps {
  sx?: SxProps;
  className?: string;

  /**
   * tint 컬러
   */
  tint: string;

  /**
   * css의 background
   * 값을 전달하지 않으면 tint 색상이 배경색
   */
  background?: string;

  /**
   * tint의 반대 컬러
   */
  contrastColor?: string;

  /**
   * 제목
   */
  title: string;

  /**
   * 배경 이미지
   */
  bgImgSrc?: string;

  /**
   * 브레드크럼 Props
   */
  breadcrumbProps?: BannerBreadcrumbsProps;

  /**
   * 탭목록
   */
  tabs?: readonly { key: string; label: React.ReactNode }[];

  /**
   * 현재 탭, 지정하지 않으면
   * 첫번째 탭으로 선택
   */
  tabKey?: string;

  /**
   * 탭변경 핸들러
   */
  onChangeTab?: (e: React.SyntheticEvent, value: string) => void;
  /**
   * 검색창
   */
  searchBar?: boolean;
}

export function PageBanner(props: PageBannerProps) {
  const {
    sx,
    className,
    title,
    tint,
    background,
    tabs,
    contrastColor = '#222',
    bgImgSrc,
    breadcrumbProps,
    tabKey,
    onChangeTab,
    searchBar = false,
  } = props;
  // const currentTab = useMemo<string>(() => (tabKey ? tabKey : tabs[0].key), [tabKey, tabs]);
  // const handleChangeTab = (e: React.SyntheticEvent, value: string) => {
  //   const tab = tabs.find((it) => it.key === value);
  //   if (tab) {
  //     onChangeTab(e, tab.key);
  //   }
  // };

  return (
    <Box
      className={clsx('PageBanner-root', className)}
      sx={flatSx(
        {
          background: background ?? tint,
          position: 'relative',
          color: contrastColor,
          height: BANNER_HEIGHT,
          overflow: 'hidden',
        },
        sx,
      )}
    >
      <Box
        className="PageBanner-bgShadow"
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          background: `linear-gradient(to bottom, ${alpha('#000', 0)} 70%, ${alpha('#000', 0.85)} 100%)`,
          zIndex: 1,
        }}
      />
      <FlexColumn
        sx={{
          width: '100%',
          maxWidth: 'lg',
          mx: 'auto',
          height: '100%',
        }}
      >
        {bgImgSrc && (
          <SmOrUp>
            <Box
              className="PageBanner-img"
              sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                '& img': {
                  height: BANNER_HEIGHT,
                  width: 788,
                },
              }}
            >
              <img src={bgImgSrc} alt="" width={788} height={210} />
            </Box>
          </SmOrUp>
        )}
        {breadcrumbProps && <BannerBreadcrumbs {...breadcrumbProps} />}
        <Typography
          className="PageBanner-title"
          component="div"
          sx={{
            ...sxFlexRowCenter(),
            flex: 1,
            fontWeight: 600,
            fontSize: '2.3rem',
            color: '#fff',
            zIndex: 999,
          }}
        >
          {title}
        </Typography>

        {tabs && tabs.length > 0 && onChangeTab && (
          <PageTabs
            className="PageBanner-pageTabs"
            tabs={tabs}
            key={tabKey}
            currentTab={tabKey ? tabKey : tabs[0].key}
            onChangeTab={(e: React.SyntheticEvent, value: string) => {
              const tab = tabs.find((it) => it.key === value);
              if (tab) {
                onChangeTab(e, tab.key);
              }
            }}
            sx={{
              zIndex: 2,
            }}
          />
        )}
      </FlexColumn>
    </Box>
  );
}
